import { enhancedApi as generatedBillingV2Api } from '@store/generated/billing-v2';

// Re-export all generated types for convenience
export type {
  ActivityFeedEvent,
  ActivityFeedGroup,
  AttentionDisposition,
  AttentionLane,
  BillingOverviewResponse,
  BillingPayerOptionRow,
  BillingPayerOptionsResponse,
  ChargeLineItem,
  ChargeLinePreviewEncounter,
  ChargeLinePreviewLine,
  ChargeLinePreviewRequest,
  ChargeLinePreviewResponse,
  ClaimCase,
  ClaimCaseDetailResponse,
  ClaimCaseListItem,
  ClaimCaseListResponse,
  ClaimCaseReviewDetail,
  ClaimCaseReviewResult,
  ClaimsV2Response,
  ClaimV2Item,
  EncounterAdjustmentsSplit,
  EncounterClaimSummary,
  EncounterLedgerGroup,
  EraTriage,
  FlowHealthRow,
  GenerateClaimCaseReviewResponse,
  LedgerEntryItem,
  PatientBillingQueueResponse,
  PatientBillingQueueSummary,
  PatientBillingStatus,
  PatientFinancialSummaryResponse,
  PatientFinancialSummaryTotals,
  PatientInvoiceHistoryItem,
  PatientQueueItem,
  PatientResponsibilityBreakdown,
  RemittancesV2Response,
  RemittanceV2Item,
  SegmentCount,
  SentInvoiceItem,
  SentInvoicesResponse,
  SentInvoicesSummary,
  SummaryStrip,
  WhatToDo,
  WorkGroup,
  WorkItem,
  WorkQueueResponse,
} from '@store/generated/billing-v2';

// Cache tag constants
const V2_TAGS = {
  OVERVIEW: 'BillingV2Overview',
  WORK: 'BillingV2Work',
  CLAIMS: 'BillingV2Claims',
  CLAIM_CASES: 'BillingV2ClaimCases',
  REMITTANCES: 'BillingV2Remittances',
  PATIENT_BILLING: 'BillingV2PatientBilling',
  PATIENT_DETAIL: 'BillingV2PatientDetail',
} as const;

// Enhance generated API with cache tags
const billingV2Api = generatedBillingV2Api.enhanceEndpoints({
  addTagTypes: Object.values(V2_TAGS),
  endpoints: {
    getV2ProviderBillingV2Overview: {
      providesTags: [V2_TAGS.OVERVIEW],
    },
    getV2ProviderBillingV2Work: {
      providesTags: [V2_TAGS.WORK],
    },
    getV2ProviderBillingV2Claims: {
      providesTags: [V2_TAGS.CLAIMS],
    },
    getV2ProviderBillingV2ClaimCases: {
      providesTags: [V2_TAGS.CLAIM_CASES],
    },
    getV2ProviderBillingV2ClaimCasesByClaimId: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.CLAIM_CASES, id: arg.claimId },
      ],
    },
    postV2ProviderBillingV2ClaimCasesByClaimIdReview: {
      invalidatesTags: (_result, _err, arg) => [
        V2_TAGS.CLAIM_CASES,
        { type: V2_TAGS.CLAIM_CASES, id: arg.claimId },
      ],
    },
    getV2ProviderBillingV2Remittances: {
      providesTags: [V2_TAGS.REMITTANCES],
    },
    getV2ProviderBillingV2PatientBillingQueue: {
      providesTags: [V2_TAGS.PATIENT_BILLING],
    },
    getV2ProviderBillingV2PatientBillingSent: {
      providesTags: [V2_TAGS.PATIENT_BILLING],
    },
    getV2ProviderBillingV2PatientsByPatientIdFinancialSummary: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.PATIENT_DETAIL, id: arg.patientId },
      ],
    },
    postV2ProviderChargeLinesPreview: {
      // Read-only preview — no cache invalidation. The mutation shape is a BE
      // choice (POST so the long ID list goes in the body); we treat it as a
      // query in the wrapper hook.
    },
  },
});

/** Credit-allocation request/response (gdn-2950), typed locally until the
 * OpenAPI codegen carries the endpoint post-merge. */
export interface CreditAllocationRequest {
  /** Client-generated idempotency key — stable across retries. */
  allocation_id: string;
  target_encounter_id: string;
  /** Null/omitted = the account-level unapplied credit. */
  source_encounter_id?: string | null;
  amount_cents: number;
}

export interface CreditAllocationResponse {
  allocation_id: string;
  amount_cents: number;
  source_encounter_id: string | null;
  target_encounter_id: string;
}

/** Refund verb (gdn-2978), typed locally until the OpenAPI codegen carries
 * the endpoints post-merge. Refunds are payment-anchored: the biller picks
 * a specific payment at the credit's scope, and the amount caps at
 * min(that payment's un-refunded remainder, the scope's cash-backed
 * credit). */
export interface RefundablePayment {
  payment_ledger_entry_id: string;
  date: string | null;
  amount_cents: number;
  refunded_cents: number;
  refundable_remainder_cents: number;
  /** Stripe-sourced payments refund through Stripe (slice 3); external
   * payments are recorded here after the practice pays out itself. */
  provenance: 'stripe' | 'external';
  description: string | null;
}

export interface RefundablePaymentsResponse {
  /** Newest-first, per the BE. */
  payments: RefundablePayment[];
  available_credit_cents: number;
  source_encounter_id: string | null;
}

export interface PatientRefundRequest {
  /** Client-generated idempotency key — stable across retries. */
  refund_id: string;
  payment_ledger_entry_id: string;
  amount_cents: number;
  /** Follows the selected payment's provenance: 'stripe' refunds to the
   * card now, 'external' records a refund issued elsewhere. */
  method: 'external' | 'stripe';
  note?: string;
}

export interface PatientRefundResponse {
  refund_id: string;
  refund_ledger_entry_id: string;
  payment_ledger_entry_id: string;
  amount_cents: number;
  method: string;
  source_encounter_id: string | null;
}

// Account-history PDF (gdn-2942) and credit allocation (gdn-2950).
// Hand-injected rather than generated: the generated slice is rebuilt from
// the *deployed* OpenAPI spec, so a generated hook for a not-yet-deployed
// endpoint would break builds. The endpoints 404 until the backend ships;
// both surfaces live behind the patient-balance-table gate.
const billingV2ApiWithDocuments = billingV2Api
  .enhanceEndpoints({
    // Cross-slice tags owned by api.ts — re-declared here so this chain's
    // types know them (runtime registration is shared and idempotent).
    addTagTypes: [
      'PatientBalance',
      'PatientInvoices',
      'BillingEncounter',
      'BillingEncountersList',
      'Eligibility',
      'Worklist',
    ],
  })
  .injectEndpoints({
  endpoints: builder => ({
    getPatientAccountHistoryPdf: builder.query<Blob, { patientId: string }>({
      query: ({ patientId }) => ({
        url: `/v2/provider/billing/v2/patients/${patientId}/account-history`,
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),
    postCreditAllocation: builder.mutation<
      CreditAllocationResponse,
      { patientId: string; body: CreditAllocationRequest }
    >({
      query: ({ patientId, body }) => ({
        url: `/v2/provider/billing/v2/patients/${patientId}/credit-allocations`,
        method: 'POST',
        body,
      }),
      // Money moved between ledger scopes — refresh the same billing
      // surfaces the reverse-charge verb does (see api.ts), plus this
      // patient's financial summary. One over-broad refetch beats a stale
      // balance.
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: V2_TAGS.PATIENT_DETAIL, id: patientId },
        V2_TAGS.PATIENT_BILLING,
        V2_TAGS.OVERVIEW,
        { type: 'PatientBalance' as const },
        { type: 'PatientInvoices' as const },
        { type: 'BillingEncounter' as const },
        { type: 'BillingEncountersList' as const, id: 'LIST' },
        { type: 'Eligibility' as const },
        { type: 'Worklist' as const },
      ],
    }),
    getRefundablePayments: builder.query<
      RefundablePaymentsResponse,
      { patientId: string; sourceEncounterId: string | null }
    >({
      query: ({ patientId, sourceEncounterId }) => ({
        url: `/v2/provider/billing/v2/patients/${patientId}/refundable-payments`,
        ...(sourceEncounterId
          ? { params: { source_encounter_id: sourceEncounterId } }
          : {}),
      }),
      // Rides the patient-detail tag so any money verb refreshes it.
      providesTags: (_result, _error, { patientId }) => [
        { type: V2_TAGS.PATIENT_DETAIL, id: patientId },
      ],
    }),
    postPatientRefund: builder.mutation<
      PatientRefundResponse,
      { patientId: string; body: PatientRefundRequest }
    >({
      query: ({ patientId, body }) => ({
        url: `/v2/provider/billing/v2/patients/${patientId}/refunds`,
        method: 'POST',
        body,
      }),
      // Money left the ledger — same refresh set as the allocation verb.
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: V2_TAGS.PATIENT_DETAIL, id: patientId },
        V2_TAGS.PATIENT_BILLING,
        V2_TAGS.OVERVIEW,
        { type: 'PatientBalance' as const },
        { type: 'PatientInvoices' as const },
        { type: 'BillingEncounter' as const },
        { type: 'BillingEncountersList' as const, id: 'LIST' },
        { type: 'Eligibility' as const },
        { type: 'Worklist' as const },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetPatientAccountHistoryPdfQuery: useLazyAccountHistoryPdfQuery,
  usePostCreditAllocationMutation: useCreditAllocationMutation,
  useGetRefundablePaymentsQuery: useRefundablePaymentsQuery,
  usePostPatientRefundMutation: usePatientRefundMutation,
} = billingV2ApiWithDocuments;

// Export user-friendly hooks
export const {
  useGetV2ProviderBillingV2OverviewQuery: useOverviewQuery,
  useGetV2ProviderBillingV2WorkQuery: useWorkQueueQuery,
  useGetV2ProviderBillingV2ClaimsQuery: useClaimsV2Query,
  useGetV2ProviderBillingV2ClaimCasesQuery: useClaimCasesQuery,
  useGetV2ProviderBillingV2ClaimCasesByClaimIdQuery: useClaimCaseDetailQuery,
  usePostV2ProviderBillingV2ClaimCasesByClaimIdReviewMutation: useGenerateClaimCaseReviewMutation,
  useGetV2ProviderBillingV2RemittancesQuery: useRemittancesV2Query,
  useGetV2ProviderBillingV2PatientBillingQueueQuery: usePatientBillingQueueQuery,
  useGetV2ProviderBillingV2PatientBillingSentQuery: usePatientBillingSentQuery,
  useGetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryQuery: usePatientFinancialSummaryQuery,
  usePostV2ProviderChargeLinesPreviewMutation: useChargeLinesPreviewMutation,
  useGetV2ProviderBillingV2PayerOptionsQuery: useBillingPayerOptionsQuery,
} = billingV2Api;

export { billingV2Api, V2_TAGS };
